import { z } from 'zod';

/**
 * Fee reminder days.
 *
 * Stored in `system_settings.auto_fee_reminder_schedule` as short codes —
 * `T-7` (7 days before the due date) and `overdue+7` (7 days after it) —
 * which stay internal. Everything a person reads goes through
 * {@link describeFeeReminder}, e.g. "7 days before the due date".
 */

/** Fees are due on this day of each month (set when the month's fees are created). */
export const FEE_DUE_DAY_OF_MONTH = 5;

export const FEE_REMINDER_MAX = 8;
export const FEE_REMINDER_MAX_DAYS_BEFORE = 28;
export const FEE_REMINDER_MAX_DAYS_AFTER = 60;

export interface FeeReminder {
  when: 'before' | 'after';
  days: number;
}

const CODE = /^(?:T-([1-9]\d?)|overdue\+([1-9]\d?))$/u;

export function parseFeeReminder(code: string): FeeReminder | null {
  const m = CODE.exec(code);
  if (!m) return null;
  if (m[1] !== undefined) {
    const days = Number(m[1]);
    return days <= FEE_REMINDER_MAX_DAYS_BEFORE ? { when: 'before', days } : null;
  }
  const days = Number(m[2]);
  return days <= FEE_REMINDER_MAX_DAYS_AFTER ? { when: 'after', days } : null;
}

export const feeReminderCode = (r: FeeReminder): string => (r.when === 'before' ? `T-${r.days}` : `overdue+${r.days}`);

/** Signed days from the due date: −7 for 7 days before, +7 for 7 days after. */
export const feeReminderOffset = (r: FeeReminder): number => (r.when === 'before' ? -r.days : r.days);

const dayWord = (n: number) => `${n} day${n === 1 ? '' : 's'}`;

/** "7 days before the due date" · "1 day after the due date" */
export const describeFeeReminder = (r: FeeReminder): string =>
  `${dayWord(r.days)} ${r.when === 'before' ? 'before' : 'after'} the due date`;

/** Earliest first: 7 days before, 3 days before, 1 day after, 7 days after. */
export const sortFeeReminders = (codes: readonly string[]): string[] =>
  [...codes].sort((a, b) => feeReminderOffset(parseFeeReminder(a)!) - feeReminderOffset(parseFeeReminder(b)!));

const ordinal = (n: number): string => {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th'}`;
};

/**
 * Why a reminder can never be sent, in plain words — or null when it's fine.
 * A "before" reminder falls on (due day − days); if the month's fees are only
 * created after that, there's nothing to remind about yet.
 */
export function feeReminderWarning(
  r: FeeReminder,
  opts: { feesCreatedOnDay: number | null; dueDay?: number },
): string | null {
  if (r.when === 'after') return null;
  const dueDay = opts.dueDay ?? FEE_DUE_DAY_OF_MONTH;
  const sendDay = dueDay - r.days;
  if (opts.feesCreatedOnDay === null) {
    return sendDay < 1
      ? `Fees are due on the ${ordinal(dueDay)}, so this reminder falls in the previous month. It only goes out if that month's fees were created by then.`
      : null;
  }
  if (sendDay < 1) {
    return `Fees are due on the ${ordinal(dueDay)}, so this reminder would fall in the previous month — before the fees are created on the ${ordinal(opts.feesCreatedOnDay)}. It won't be sent.`;
  }
  if (sendDay < opts.feesCreatedOnDay) {
    return `This reminder would go out on the ${ordinal(sendDay)}, but the month's fees are only created on the ${ordinal(opts.feesCreatedOnDay)}. It won't be sent.`;
  }
  return null;
}

export const feeReminderCodeSchema = z
  .string()
  .refine((v) => parseFeeReminder(v) !== null, 'Choose 1–28 days before or 1–60 days after the due date');

export const feeReminderScheduleSchema = z
  .array(feeReminderCodeSchema)
  .max(FEE_REMINDER_MAX, `Up to ${FEE_REMINDER_MAX} reminders`)
  .refine((list) => new Set(list).size === list.length, 'Each reminder can only be added once');
